import React from 'react';
import { FieldImage, Fit, RawText, Text, fs } from '../parts/fields';
import { useFieldSize } from '../parts/chrome';
import { bool, img, str, useSlide } from '../parts/context';
import { bindingParts } from '../text';

const DEFAULT_PREPARED = 'Prepared for {{lead.company}}';

// #region CoverLayout
/** The first slide: logo, big title, who it's for. With an image, the image takes the right side in the brand frame. */
export const CoverLayout: React.FunctionComponent = (): React.ReactElement => {
    const { slide, context, edit, onFieldChange } = useSlide();
    const withImage = edit || !!img(slide.fields.image);
    const size = useFieldSize('title', withImage ? [[22, 112], [40, 96], [64, 82], [999, 70]] : [[26, 136], [48, 116], [70, 98], [999, 84]]);
    const subSize = useFieldSize('subtitle', [[70, 38], [120, 34], [999, 30]]);
    const preparedRaw = slide.fields.prepared_for === undefined || slide.fields.prepared_for === null ? DEFAULT_PREPARED : str(slide.fields.prepared_for);
    // "Prepared for {{lead.company}}" with no lead would read "Prepared for": leave it out of the view instead.
    const bindings = bindingParts(preparedRaw, context.values).filter((p) => p.kind === 'binding');
    const allEmpty = bindings.length > 0 && bindings.every((p) => p.kind === 'binding' && !p.text.includes('|') && !(context.values[p.key] || '').trim());
    const showPrepared = edit || !allEmpty;
    const date = bool(slide.fields.show_date, true) ? context.values.today : '';
    const logo = context.theme.logo_url;

    return (
        <>
            {withImage ? null : <Glow />}
            <div className="wd-pad" style={{ paddingTop: 96, paddingBottom: 96, right: withImage ? 880 : 0 }}>
                {logo ? <img src={logo} alt={context.theme.name} style={{ height: 52, width: 'auto', maxWidth: 300, objectFit: 'contain', objectPosition: 'left', alignSelf: 'flex-start' }} draggable={false} /> : <div style={{ height: 52 }} />}
                <Fit style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 48, paddingBottom: 48 }}>
                    <Text k="eyebrow" className="wd-eyebrow" placeholder="Eyebrow" max={40} style={{ fontSize: fs(24), marginBottom: 36 }} />
                    <Text k="title" as="h1" className="wd-h" style={{ fontSize: fs(size), lineHeight: 1.02, maxWidth: withImage ? 860 : 1500 }} placeholder="Deck title" max={90} />
                    <Text k="subtitle" className="wd-lead" style={{ fontSize: fs(subSize), marginTop: 40, maxWidth: withImage ? 820 : 1180, color: 'var(--wd-body)' }} placeholder="Subtitle" max={160} />
                </Fit>
                <footer style={{ display: 'flex', alignItems: 'center', gap: 28, flex: 'none', minHeight: 44 }}>
                    <span className="wd-rule" style={{ width: 64 }} />
                    {showPrepared ? (
                        <RawText raw={preparedRaw} fieldKey="prepared_for" onChange={(v) => onFieldChange?.('prepared_for', v)} className="wd-h"
                            style={{ fontSize: 32, letterSpacing: 0 }} placeholder="Prepared for" max={80} />
                    ) : null}
                    {date ? <span className="wd-muted" style={{ fontSize: 28 }}>{showPrepared ? '· ' : ''}{date}</span> : null}
                </footer>
            </div>
            {withImage ? (
                <FieldImage k="image" framed className="wd-cover-img" label="Add a cover image"
                    style={{ position: 'absolute', top: 56, right: 56, bottom: 56, width: 800 }} />
            ) : null}
        </>
    );
};

// #region Glow
/** A soft wash of the brand gradient for covers without an image. */
const Glow: React.FunctionComponent = (): React.ReactElement => (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 1300, height: 1300, right: -420, top: -360, borderRadius: '50%', background: 'var(--wd-frame)', opacity: 0.16, filter: 'blur(4px)' }} />
        <div style={{ position: 'absolute', width: 760, height: 760, right: -60, top: 120, borderRadius: '50%', border: '3px solid var(--wd-accent)', opacity: 0.18 }} />
        <div style={{ position: 'absolute', width: 420, height: 420, right: 300, bottom: -180, borderRadius: '50%', background: 'var(--wd-secondary)', opacity: 0.22 }} />
    </div>
);
